import * as THREE from 'three'

export default class InformationSection
{
    constructor(_options)
    {
        // Options
        this.time = _options.time
        this.resources = _options.resources
        this.objects = _options.objects
        this.areas = _options.areas
        this.tiles = _options.tiles
        this.debug = _options.debug
        this.x = _options.x
        this.y = _options.y

        // Set up
        this.container = new THREE.Object3D()
        this.container.matrixAutoUpdate = false

        this.setStatic()
        this.setBaguettes()
        this.setLinks()
        this.setExperienceFloor()
        this.setTiles()
    }

    setStatic()
    {
        this.objects.add({
            base: this.resources.items.informationStaticBase.scene,
            collision: this.resources.items.informationStaticCollision.scene,
            floorShadowTexture: this.resources.items.informationStaticFloorShadowTexture,
            offset: new THREE.Vector3(this.x, this.y, 0),
            mass: 0
        })
    }

    setBaguettes()
    {
        this.baguettes = {}

        this.baguettes.x = - 4
        this.baguettes.y = 6

        this.baguettes.a = this.objects.add({
            base: this.resources.items.informationBaguetteBase.scene,
            collision: this.resources.items.informationBaguetteCollision.scene,
            offset: new THREE.Vector3(this.x + this.baguettes.x - 0.56, this.y + this.baguettes.y - 0.666, 0.2),
            rotation: new THREE.Euler(0, 0, - Math.PI * 37 / 180),
            duplicated: true,
            shadow: { sizeX: 0.6, sizeY: 3.5, offsetZ: - 0.15, alpha: 0.35 },
            mass: 1.5,
        })

        this.baguettes.b = this.objects.add({
            base: this.resources.items.informationBaguetteBase.scene,
            collision: this.resources.items.informationBaguetteCollision.scene,
            offset: new THREE.Vector3(this.x + this.baguettes.x - 0.8, this.y + this.baguettes.y - 2, 0.5),
            rotation: new THREE.Euler(0, - 0.5, Math.PI * 60 / 180),
            duplicated: true,
            shadow: { sizeX: 0.6, sizeY: 3.5, offsetZ: - 0.15, alpha: 0.35 },
            mass: 1.5,
            sleep: false,
        })
    }

    // Helper to create canvas texture for labels
    createLabelTexture(mainText, subText = '')
    {
        const canvas = document.createElement('canvas')
        canvas.width = 256
        canvas.height = 64
        const ctx = canvas.getContext('2d')

        ctx.clearRect(0, 0, 256, 64)

        ctx.fillStyle = '#ffffff'
        ctx.font = 'bold 28px Inter, Arial, sans-serif'
        ctx.textAlign = 'left'
        ctx.textBaseline = 'top'
        ctx.fillText(mainText.toUpperCase(), 8, 8)

        if (subText) {
            ctx.font = '14px Inter, Arial, sans-serif'
            ctx.fillStyle = 'rgba(255, 255, 255, 0.7)'
            ctx.fillText(subText, 8, 40)
        }

        const texture = new THREE.CanvasTexture(canvas)
        texture.needsUpdate = true
        texture.magFilter = THREE.NearestFilter
        texture.minFilter = THREE.LinearFilter
        return texture
    }

    // Helper to create experience card texture
    createExperienceTexture(role, company, period, description)
    {
        const canvas = document.createElement('canvas')
        canvas.width = 512
        canvas.height = 256
        const ctx = canvas.getContext('2d')

        ctx.clearRect(0, 0, 512, 256)

        // Role
        ctx.fillStyle = '#ffffff'
        ctx.font = 'bold 32px Inter, Arial, sans-serif'
        ctx.textAlign = 'left'
        ctx.textBaseline = 'top'
        ctx.fillText(role, 20, 20)

        // Company
        ctx.font = 'bold 24px Inter, Arial, sans-serif'
        ctx.fillStyle = '#00ff88'
        ctx.fillText(company, 20, 60)

        // Period
        ctx.font = '18px Inter, Arial, sans-serif'
        ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
        ctx.fillText(period, 20, 95)

        // Description - word wrap
        ctx.font = '16px Inter, Arial, sans-serif'
        ctx.fillStyle = 'rgba(255, 255, 255, 0.85)'
        const words = description.split(' ')
        let line = ''
        let y = 130
        const maxWidth = 470
        for (let i = 0; i < words.length; i++) {
            const testLine = line + words[i] + ' '
            const metrics = ctx.measureText(testLine)
            if (metrics.width > maxWidth && i > 0) {
                ctx.fillText(line, 20, y)
                line = words[i] + ' '
                y += 22
                if (y > 230) break
            } else {
                line = testLine
            }
        }
        ctx.fillText(line, 20, y)

        const texture = new THREE.CanvasTexture(canvas)
        texture.needsUpdate = true
        texture.magFilter = THREE.NearestFilter
        texture.minFilter = THREE.LinearFilter
        return texture
    }

    setLinks()
    {
        // Set up
        this.links = {}
        this.links.x = 1.95
        this.links.y = - 1.5
        this.links.halfExtents = {}
        this.links.halfExtents.x = 1
        this.links.halfExtents.y = 1
        this.links.distanceBetween = 2.4
        this.links.labelWidth = this.links.halfExtents.x * 2 + 1
        this.links.labelGeometry = new THREE.PlaneGeometry(this.links.labelWidth, this.links.labelWidth * 0.25, 1, 1)
        this.links.labelOffset = - 1.6
        this.links.items = []

        this.links.container = new THREE.Object3D()
        this.links.container.matrixAutoUpdate = false
        this.container.add(this.links.container)

        // 4 social links
        this.links.options = [
            {
                href: 'https://scholar.google.com/citations?user=5BawrcEAAAAJ&hl=en',
                labelTexture: this.createLabelTexture('Scholar', '200+ citations')
            },
            {
                href: 'https://github.com/giantrksa',
                labelTexture: this.createLabelTexture('GitHub', 'giantrksa')
            },
            {
                href: 'https://www.linkedin.com/in/antrks/',
                labelTexture: this.createLabelTexture('LinkedIn', 'antrks')
            },
            {
                href: 'mailto:gian.antariksa@gmail.com',
                labelTexture: this.createLabelTexture('Mail', 'gian.antariksa@gmail.com')
            }
        ]

        let i = 0
        for(const _option of this.links.options)
        {
            const item = {}
            item.x = this.x + this.links.x + this.links.distanceBetween * i
            item.y = this.y + this.links.y
            item.href = _option.href

            item.area = this.areas.add({
                position: new THREE.Vector2(item.x, item.y),
                halfExtents: new THREE.Vector2(this.links.halfExtents.x, this.links.halfExtents.y)
            })
            item.area.on('interact', () =>
            {
                window.open(_option.href, '_blank')
            })

            item.labelMesh = new THREE.Mesh(this.links.labelGeometry, new THREE.MeshBasicMaterial({ wireframe: false, color: 0xffffff, map: _option.labelTexture, depthTest: true, depthWrite: false, transparent: true }))
            item.labelMesh.position.x = item.x + this.links.labelWidth * 0.5 - this.links.halfExtents.x
            item.labelMesh.position.y = item.y + this.links.labelOffset
            item.labelMesh.matrixAutoUpdate = false
            item.labelMesh.updateMatrix()
            this.links.container.add(item.labelMesh)

            this.links.items.push(item)
            i++
        }
    }

    setExperienceFloor()
    {
        // Experience cards on the floor with detailed descriptions from resume
        this.experience = {}
        this.experience.container = new THREE.Object3D()
        this.experience.container.matrixAutoUpdate = false
        this.container.add(this.experience.container)

        // Experience data from resume
        const experiences = [
            {
                role: 'DATA SCIENTIST',
                company: 'Philip Morris International',
                period: 'Mar 2026 - Present',
                description: 'Consumer segmentation with K-Means, DBSCAN, GMM. Demand forecasting using LSTM, TFT, Prophet. Serverless ETL on AWS with real-time dashboards.'
            },
            {
                role: 'SENIOR DATA SCIENTIST',
                company: 'PT. Mitra Solusi Telematika',
                period: 'May 2024 - Feb 2026',
                description: 'Agentic AI for SAP with RAG pipeline. CV Screening AI Agent. LLM Router with MCP integration. 60% LLM cost reduction.'
            },
            {
                role: 'POSTDOCTORAL FELLOW',
                company: 'Texas State University, USA',
                period: 'Jan 2024 - May 2024',
                description: 'AutoML with XAI for pedestrian crash prediction. Published in IEEE TITS (Q1, IF:8.5). BERTopic + LLAMA-2 integration.'
            },
            {
                role: 'AI ENGINEER',
                company: 'INEEJI, South Korea',
                period: 'Sept 2023 - Jan 2024',
                description: 'INFINITE OPTIMAL SERIES: Deep Learning Anomaly Detection with XAI. AutoML pipeline. Deployed on GCP with CUDA. Sold to Korea & Japan.'
            },
            {
                role: 'DATA SCIENCE SPECIALIST',
                company: 'PT. Cybertrend Intrabuana',
                period: 'Aug 2021 - May 2024',
                description: 'CybertrendGPT RAG Platform. CtrendVision for Retail. CtrendMax for Manufacturing XAI. Sold to 30+ companies in SE Asia.'
            },
            {
                role: 'AI SCIENTIST',
                company: 'Pukyong National University',
                period: 'Sept 2020 - Aug 2023',
                description: 'Maritime XAI anomaly detection. ML-based lithofacies classification. Deep sequence well log imputation. 6+ journal publications.'
            }
        ]

        const cardGeometry = new THREE.PlaneGeometry(8, 4)
        const startX = this.x - 10
        const startY = this.y - 8
        const spacingX = 9
        const spacingY = 5

        experiences.forEach((exp, index) => {
            const col = index % 2
            const row = Math.floor(index / 2)

            const cardTexture = this.createExperienceTexture(exp.role, exp.company, exp.period, exp.description)
            const cardMaterial = new THREE.MeshBasicMaterial({
                map: cardTexture,
                transparent: true,
                depthWrite: false
            })

            const cardMesh = new THREE.Mesh(cardGeometry, cardMaterial)
            cardMesh.position.set(startX + col * spacingX, startY - row * spacingY, 0.01)
            cardMesh.matrixAutoUpdate = false
            cardMesh.updateMatrix()
            this.experience.container.add(cardMesh)
        })
    }

    setTiles()
    {
        this.tiles.add({
            start: new THREE.Vector2(this.x - 1.2, this.y + 13),
            delta: new THREE.Vector2(0, - 20)
        })
    }
}
